import React, { useEffect, useRef } from 'react';

/**
 * ReactiveAudioWaveform
 * Hardware-accelerated canvas audio visualizer driven directly by Web Audio AnalyserNode.
 * 
 * Features:
 * - 3-Zone (Tri-part) physics distribution:
 *   1. Left Wing (Outer ~24%): Smallest height, soft & gentle responsiveness.
 *   2. Center Dominant Section (Middle ~52%): Highest length, dynamic mix of aggressive & medium peaks.
 *   3. Right Wing (Outer ~24%): Symmetrical equal length to left wing, soft & gentle.
 * - Hardware canvas rendering with zero React re-renders in the animation loop.
 */
export function ReactiveAudioWaveform({
  analyser = null,
  analyserNode = null,
  mode = 'speaking', // 'speaking' | 'listening' | 'idle'
  barCount = 21,
  numBars = null,
  width = 260,
  height = 52,
  speakingColor = '#F59E0B', // Warm amber / orange
  listeningColor = '#818CF8', // Indigo / violet
  idleColor = 'rgba(148, 163, 184, 0.4)',
  isActive = true,
}) {
  const effectiveAnalyser = analyser || analyserNode;
  const effectiveBarCount = numBars || barCount;
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const smoothedHeightsRef = useRef(new Float32Array(effectiveBarCount).fill(4));
  const phaseRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Handle high-DPI displays
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    const dataArray = effectiveAnalyser
      ? new Uint8Array(effectiveAnalyser.frequencyBinCount)
      : null;

    const totalBars = effectiveBarCount;
    const barWidth = 4.5;
    const gap = (width - totalBars * barWidth) / Math.max(1, totalBars - 1);
    const minHeight = 4;
    const maxHeight = height - 6;

    const render = () => {
      ctx.clearRect(0, 0, width, height);

      let hasActiveAudio = false;
      const rawFrequencies = [];

      if (effectiveAnalyser && dataArray && isActive) {
        effectiveAnalyser.getByteFrequencyData(dataArray);

        // Calculate vocal speech energy in speech spectrum (bins 2 to 45)
        let totalEnergy = 0;
        const binCountToSample = Math.min(dataArray.length, 45);
        for (let i = 2; i < binCountToSample; i++) {
          totalEnergy += dataArray[i];
        }
        const avgEnergy = totalEnergy / Math.max(1, binCountToSample - 2);

        // Sensitive vocal activation threshold
        if (avgEnergy > 4) {
          hasActiveAudio = true;
        }

        const center = (totalBars - 1) / 2;

        for (let i = 0; i < totalBars; i++) {
          const distFromCenter = Math.abs(i - center);
          const normDist = distFromCenter / Math.max(1, center); // 0 (center) to 1.0 (tips)

          // Sample voice frequencies mapped around formant bands
          const binIndex = Math.min(
            dataArray.length - 1,
            Math.floor(2 + (1 - normDist) * 26 + (i % 3) * 2)
          );
          const binValue = dataArray[binIndex] || 0;

          // Normalize audio input
          const normalized = Math.min(1, Math.max(0, (binValue - 3) / 90));

          let targetH;

          // TRI-ZONE VISUAL DISTRIBUTION:
          // Middle Zone: normDist <= 0.52 (~52% width, e.g. center 11 of 21 bars)
          // Edge Zones: normDist > 0.52 (~24% each side, e.g. outer 5 bars on left & right)
          if (normDist <= 0.52) {
            // Middle section: Dominant, highest peaks with mix of aggressive and medium bars
            const middleProgress = normDist / 0.52;
            const middleEnvelope = 0.72 + 0.28 * Math.cos(middleProgress * (Math.PI / 2));
            // Harmonic rhythmic variation: alternating bars between high aggressive (1.0) and medium aggressive (0.80)
            const harmonicPattern = (i % 2 === 0) ? 1.0 : 0.80;
            const zoneMultiplier = middleEnvelope * harmonicPattern;
            
            // Aggressive non-linear boost for speech punchiness
            const boosted = Math.pow(normalized, 0.55);
            targetH = minHeight + boosted * zoneMultiplier * (maxHeight - minHeight);
          } else {
            // Edge sections (Left & Right outer wings): Equal length, smallest height, gentle
            const edgeProgress = (normDist - 0.52) / 0.48;
            const wingMultiplier = Math.max(0.12, 0.35 - edgeProgress * 0.20);
            
            // Moderate responsiveness for edge bars
            const boosted = Math.pow(normalized, 0.75);
            targetH = minHeight + boosted * wingMultiplier * (maxHeight - minHeight);
          }

          rawFrequencies.push(targetH);
        }
      }

      phaseRef.current += 0.07;

      // Update smoothed heights with snappy attack and smooth decay
      const center = (totalBars - 1) / 2;
      for (let i = 0; i < totalBars; i++) {
        let target;
        const distFromCenter = Math.abs(i - center);
        const normDist = distFromCenter / Math.max(1, center);

        if (hasActiveAudio && rawFrequencies.length === totalBars) {
          target = rawFrequencies[i];
        } else {
          // Tri-zone symmetric resting breathing pulse (no left-to-right drift)
          const pulse = Math.sin(phaseRef.current) * 0.5 + 0.5;
          if (mode === 'idle' || !isActive) {
            target = minHeight;
          } else if (normDist <= 0.52) {
            // Middle zone resting pulse
            const middleProgress = normDist / 0.52;
            const middleEnvelope = 0.72 + 0.28 * Math.cos(middleProgress * (Math.PI / 2));
            const harmonicPattern = (i % 2 === 0) ? 1.0 : 0.82;
            target = minHeight + pulse * middleEnvelope * harmonicPattern * 6.5;
          } else {
            // Edge zones resting pulse (gentle & subtle)
            const edgeProgress = (normDist - 0.52) / 0.48;
            const wingMultiplier = Math.max(0.12, 0.35 - edgeProgress * 0.20);
            target = minHeight + pulse * wingMultiplier * 3.0;
          }
        }

        // Fast snappy attack (0.65) for instant vocal response, smooth release (0.18)
        const current = smoothedHeightsRef.current[i] || minHeight;
        const lerpFactor = target > current ? 0.65 : 0.18;
        smoothedHeightsRef.current[i] = current + (target - current) * lerpFactor;
      }

      // Choose color based on active mode
      const activeColor =
        mode === 'speaking'
          ? speakingColor
          : mode === 'listening'
            ? listeningColor
            : idleColor;

      ctx.fillStyle = activeColor;

      // Draw rounded vertical bars centered vertically
      for (let i = 0; i < totalBars; i++) {
        const h = Math.max(minHeight, Math.min(maxHeight, smoothedHeightsRef.current[i]));
        const x = i * (barWidth + gap);
        const y = (height - h) / 2;
        const radius = barWidth / 2;

        ctx.beginPath();
        if (typeof ctx.roundRect === 'function') {
          ctx.roundRect(x, y, barWidth, h, radius);
        } else {
          ctx.rect(x, y, barWidth, h);
        }
        ctx.fill();
      }

      animationFrameRef.current = requestAnimationFrame(render);
    };

    render();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [
    effectiveAnalyser,
    effectiveBarCount,
    isActive,
    mode,
    width,
    height,
    speakingColor,
    listeningColor,
    idleColor,
  ]);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width,
        height,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width,
          height,
          display: 'block',
        }}
      />
    </div>
  );
}

export default ReactiveAudioWaveform;
